import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useUsersStore } from "./users";
import { useReadingResultStore } from "./readingResult";
import { usePopupStore } from "./popup";
import { dicomUpload, exportDicomFile, dicomTag, getDicomList, selectFile, deleteDicomFiles, dicomVisualization, anaysisVisualization, checkLastLoginToken } from '../api';
import { buffer } from '@tensorflow/tfjs';
import { usePacsStore } from './pacs';

export interface DicomFile {
  selected: boolean;
  user_id: string;
  unique_id: number;
  origin_filename: string;
  unique_filename: string;
  patient_name: string;
  patient_id: string;
  study_date: string;
  status: string;
  score: number;
  is_reviewed: boolean;
  showIdTooltip: boolean;
  showNameTooltip: boolean;
  showDateTooltip: boolean;
  origin_png_url: string;
  anal_png_url: string;
}

interface DicomInfo {
  origin_image_url: string;
  anal_image_url: string;
  window_center: number;
  window_width: number;
  window_center_min: number;
  window_center_max: number;
  window_width_min: number;
  window_width_max: number;
  tube_threshold: number;
  image_weight: number,
  select_heatmap_idx: number;
}

export interface DicomTagItem {
  tag: string;
  description: string;
  vr: string;
  vm: number;
  value: string;
  showTooltip: boolean;
}

interface VisualizationOption {
  mask_check: boolean,
  heatmap_check: boolean,
  probability_map_check: boolean,
  heatmap_option: string
}

export const useDicomStore = defineStore('dicom', {
  state: () => ({
    dicomList: [] as DicomFile[],
    targetDicomFile: {} as DicomFile, // 현재 선택되어 있는 dicom file
    targetDicomInfo: {} as DicomInfo, // 현재 선택되어 있는 dicom file info
    defaultDicomInfo: {} as DicomInfo, // 초기값 저장
    visualizationOption: {
      mask_check: true,
      heatmap_check: true,
      probability_map_check: true,
      heatmap_option: "Complete"
    } as VisualizationOption,
    files: [] as File[],
    uploadProgress: 0,
    loading: false, // dicom option loading
    imageLoading: false, // dicom image loading
    scoreLoading: false, // dicom score confirm or reset loading
    disableFileSelect: false,
    completeScore: 0,
    dicomTag: [] as DicomTagItem[],
    pendingListCount: 0,
    activeDeleteBtn: false,
    searchRes: [] as DicomFile[], // dicom file list 검색 결과
    duplicationFileList: [] as any,
    failedFileList: [] as any,
    // tryVisualization: false,
    singleImg: false, // 현재 화면에 보여지는 이미지 개수
    dicomImg: null,
    buffers: {} as Record<string, ArrayBuffer>, // bin data
  }),
  actions: {
    setDicomImg(img: any) {
      this.dicomImg = img
    },
    setBinFiles(buffers: Record<string, ArrayBuffer>) {
      this.buffers = buffers;
    },
    initializeDefaults() {
      this.targetDicomFile = {} as DicomFile;
      this.targetDicomInfo = {} as DicomInfo;
      this.completeScore = 0;
      this.dicomTag = [] as DicomTagItem[];
      this.visualizationOption = {
        mask_check: true,
        heatmap_check: true,
        probability_map_check: true,
        heatmap_option: "Complete"
      } as VisualizationOption;
    },
    initializeOptions() {
      // this.tryVisualization = false;
      // this.visualizationOption = {
        // mask_check: true,
        // heatmap_check: true,
        // probability_map_check: true,
        // heatmap_option: "Complete"
      // } as VisualizationOption;
      this.targetDicomInfo = { ...this.defaultDicomInfo };
    },
    setFile(files: File[]) {
      this.pendingListCount += files.length;
      this.files = files;
      return true;
    },
    insertDicomList(socketRes: any) {
      const usersStore = useUsersStore();
      const withCAD = usersStore.getWithCAD;

      if (this.pendingListCount > 0) {
        this.decreasePendingCount();
      }

      if (withCAD) {
        this.dicomList.push({
          selected: false,
          is_reviewed: false,
          ...socketRes
        });
      } else {
        this.dicomList.push({
          selected: false,
          is_reviewed: false,
          ...socketRes,
          score: 0
        });
      }
    },
    checkDicomList(socketRes: any) { // 이미 대기열에 등록 되어있는 파일인지 체크
      const dicomToUpdate = this.dicomList.find(dicom => dicom.unique_filename === socketRes.unique_filename);
      if (dicomToUpdate) {
        this.updateDicomList(socketRes);
      } else {
        this.insertDicomList(socketRes);
      }
    },
    updateDicomList(socketRes: any) {
      const usersStore = useUsersStore();
      const withCAD = usersStore.getWithCAD;
      
      const dicomToUpdate = this.dicomList.find(dicom => dicom.unique_filename === socketRes.unique_filename);
      if (dicomToUpdate) {
        dicomToUpdate.status = socketRes.status;
        if (withCAD) {
          dicomToUpdate.score = socketRes.score;
        } else {
          dicomToUpdate.score = 0;
        }
      }
    },
    async deleteDicomList() {
      const res = await deleteDicomFiles();
      if(res) {
        await this.getDicomList();
        await useReadingResultStore().setReadingResultList();
      }
    },
    async getDicomList() {
      const res = await getDicomList();
      if(res) {
        this.dicomList = res.map((item: Omit<DicomFile, "selected">) => ({
          ...item,
          selected: false, // 기본값 설정
          origin_png_url: "",
          anal_png_url: ""
        }));
      }
    },
    initDicomList() {
      this.dicomList = [];
      this.searchRes = [];
    },
    async dicomUpload() {
      const dicomData = await dicomUpload((progress) => {
        this.uploadProgress = progress;
      });
    },
    setPendingCount(count: number) {
      this.pendingListCount = count;
    },
    decreasePendingCount() {
      this.pendingListCount--;
    },
    resetPendingCount() {
      this.pendingListCount = 0;
    },
    setActiveDeleteBtnState(active: boolean) {
      this.activeDeleteBtn = active;
    },
    setCheckboxState(unique_filename: string, selected: boolean) {
      if (unique_filename === "") {
        this.dicomList.forEach(dicom => {
          dicom.selected = selected;
        });
      } else {
        const target = this.dicomList.find(list => list.unique_filename === unique_filename);
        if (target) {
          target.selected = selected;
        }
      }
    },
    async getSelectedDicomFile(dicom: DicomFile) {
      const isSessionValid = await checkLastLoginToken();
      if (!isSessionValid) return;

      this.targetDicomFile = dicom;

      const selectedFileInfo = await selectFile();
      if(selectedFileInfo != undefined) {
        this.targetDicomInfo.origin_image_url = '';
        this.targetDicomInfo.anal_image_url = '';
        this.targetDicomInfo = {
          ...this.targetDicomInfo, // 기존 값 유지
          window_center: selectedFileInfo.window_center,
          window_width: selectedFileInfo.window_width,
          window_center_min: selectedFileInfo.window_center_min,
          window_center_max: selectedFileInfo.window_center_max,
          window_width_min: selectedFileInfo.window_width_min,
          window_width_max: selectedFileInfo.window_width_max,
          tube_threshold: selectedFileInfo.tube_threshold,
          image_weight: selectedFileInfo.image_weight,
          select_heatmap_idx: selectedFileInfo.select_heatmap_idx
        };
        this.defaultDicomInfo = { ...this.targetDicomInfo };
        this.getDicomTagData();
      }
    },
    setTargetDicomInfo<K extends keyof DicomInfo>(
      key: K,
      value: DicomInfo[K]
    ) {
      this.targetDicomInfo = {
        ...this.targetDicomInfo,
        [key]: value
      }
    },
    async visualization() {
      const usersStore = useUsersStore();
      const withCAD = usersStore.getWithCAD;

      this.targetDicomInfo.origin_image_url = '';
      this.targetDicomInfo.anal_image_url = '';

      const dicomVisualizationImageUrl = await dicomVisualization() ?? '';
      this.targetDicomInfo.origin_image_url = dicomVisualizationImageUrl;

      if (withCAD && (this.visualizationOption.mask_check || this.visualizationOption.heatmap_check || this.visualizationOption.probability_map_check)) {
        const analVisualizationImageUrl = await anaysisVisualization() ?? '';
        this.targetDicomInfo.anal_image_url = analVisualizationImageUrl;
      }

      this.loading = false;
    },
    setWindowOption(wc: number, ww: number) {
      this.targetDicomInfo.window_center = wc;
      this.targetDicomInfo.window_width = ww;
    },
    setExtraOption(tube: number, iw: number) {
      this.targetDicomInfo.tube_threshold = tube;
      this.targetDicomInfo.image_weight = iw;
    },
    setHeatmapOption(idx: number, option: string) {
      this.targetDicomInfo.select_heatmap_idx = idx;
      this.visualizationOption.heatmap_option = option;
    },
    setMaskCheck(checked: boolean) {
      this.visualizationOption.mask_check = checked;
    },
    setHeatMapCheck(checked: boolean) {
      this.visualizationOption.heatmap_check = checked;
    },
    setProbabilityCheck(checked: boolean) {
      this.visualizationOption.probability_map_check = checked;
    },
    async getDicomTagData() {
      this.dicomTag = await dicomTag();
    },
    initOption() {
    },
    setLoading(loading: boolean) {
      this.loading = loading;
    },
    setImageLoading(loading: boolean) {
      this.imageLoading = loading;
    },
    setScoreLoading(loading: boolean) {
      this.scoreLoading = loading;
    },
    setFileSelectDisabled(isDisabled: boolean) {
      this.disableFileSelect = isDisabled;
    },
    async handleExportFile(target: string, blob: any) {
      await exportDicomFile(target, blob);
    },
    setCompleteScore(score: number) {
      this.completeScore = score;
    },
    setSearchRes(list: DicomFile[]) {
      this.searchRes = list;
    },
    insertDuplicationFile(socketRes: any) {
      if (this.pendingListCount > 0) {
        this.decreasePendingCount();
      }
      this.duplicationFileList.push(socketRes);
    },
    initDuplicationFileList() {
      this.duplicationFileList = [];
    },
    checkDuplicationFileExist() {
      if(this.pendingListCount == 0) {
        if(this.duplicationFileList.length > 0) {
          usePopupStore().togglePopup('duplication', true);
        }
      }
    },
    insertFailedFile(file: any) {
      this.failedFileList.push(file);
    },
    initFailedFileList() {
      this.failedFileList = [];
    },
    checkFailedFileExist() {
      if (this.failedFileList.length > 0) {
        usePopupStore().togglePopup('disk', true);
      }
    },
    // setTryVisualization(bool: boolean) {
    //   this.tryVisualization = bool;
    // },
    setSingleImg(isSingle: boolean) {
      this.singleImg = isSingle;
    },
  },
  getters: {
    getDicomImg(state): any {
      return state.dicomImg
    },
    getBinFiles(state): Record<string, ArrayBuffer> {
      return state.buffers
    },
    getDicomFilesList(state): DicomFile[] {
      return state.dicomList;
    },
    getTargetDicomFile(state): DicomFile {
      return state.targetDicomFile;
    },
    getTargetDicomInfo(state): DicomInfo {
      return state.targetDicomInfo;
    },
    getVisualizationOption(state): VisualizationOption {
      return state.visualizationOption;
    },
    getFile(state): File[] {
      return state.files;
    },
    getUploadProgress(state): number {
      return state.uploadProgress;
    },
    getLoadingState(state): boolean {
      return state.loading;
    },
    getImageLoadingState(state): boolean {
      return state.imageLoading;
    },
    getScoreLoadingState(state): boolean {
      return state.scoreLoading;
    },
    getFileSelectState(state): boolean {
      return state.disableFileSelect;
    },
    getCompleteScore(state): number {
      return state.completeScore;
    },
    getDicomTag(state): any {
      return state.dicomTag;
    },
    getPendingListCount(state): number {
      return state.pendingListCount;
    },
    getActiveDeleteBtnState(state): boolean {
      return state.activeDeleteBtn;
    },
    getMaskCheckState(state): boolean {
      return state.visualizationOption.mask_check;
    },
    getHeatMapCheckState(state): boolean {
      return state.visualizationOption.heatmap_check;
    },
    getProbabilityCheckState(state): boolean {
      return state.visualizationOption.probability_map_check;
    },
    getSearchRes(state): DicomFile[] {
      return state.searchRes;
    },
    getDuplicationFileList(state): DicomFile[] {
      return state.duplicationFileList;
    },
    getFailedFileList(state): any {
      return state.failedFileList;
    },
    // getTryVisualization(state): boolean {
    //   return state.tryVisualization;
    // },
    getSingleImg(state): boolean {
      return state.singleImg;
    },
  },
});